import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  AxiosInstance,
} from 'axios';
import { ACCESS_TOKEN, APP_REFERER } from '../../constants';
import { cookiesHelper } from '../../helpers';
import { renewAccessToken } from './token.helper';
import { translateErrorMessages } from './error.utils';
import { TFunction } from 'i18next';

const axiosOptions = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const handleCancelRequest = () => {
  const cancelResult = {
    statusCode: 429,
    message: 'Cancel Request',
    error: 'Cancel Request',
  };
  return Promise.reject(cancelResult);
};

export const createAxiosInstance = (
  baseURL: string,
  appName: APP_REFERER = APP_REFERER.PAYROLLR,
  t?: TFunction, // optional i18n translator
): AxiosInstance => {
  const axiosInstance = axios.create({ ...axiosOptions, baseURL });

  axiosInstance.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const accessToken = cookiesHelper.get(ACCESS_TOKEN);
      if (accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`,
        };
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const { response, config } = error;

      if (axios.isCancel(error)) return handleCancelRequest();

      // Auto-renew access token on 401
      if (response?.status === 401) {
        try {
          return await renewAccessToken(appName, axiosInstance, config);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      if (response?.data?.details && t) {
        const rawError = response.data;
        const finalError = translateErrorMessages(rawError, t);
        return Promise.reject(finalError);
      }

      return Promise.reject(response?.data || error);
    },
  );

  return axiosInstance;
};
